import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

type VariantStyles = {
  backgroundColor: string;
  borderColor?: string;
  color: string;
};

const createVariantStyles = ({ backgroundColor, borderColor, color }: VariantStyles) => css`
  background-color: ${backgroundColor};
  color: ${color};
  border: 1px solid ${borderColor ?? 'transparent'};

  &:visited {
    background-color: ${backgroundColor};
    color: ${color};
    border-color: ${borderColor ?? 'transparent'};
  }

  &:hover:not([disabled]):not([aria-disabled='true']) {
    background-color: color-mix(
      in srgb,
      ${color} var(${themeVariables.tint.hover}),
      ${backgroundColor} calc(100% - var(${themeVariables.tint.hover}))
    );
  }

  &[disabled],
  &[aria-disabled='true'] {
    background-color: color-mix(
      in srgb,
      ${color} var(${themeVariables.tint.disabled}),
      ${backgroundColor} calc(100% - var(${themeVariables.tint.disabled}))
    );
    color: ${color};
  }
`;

export const root = css`
  appearance: none;
  border-radius: var(${themeVariables.borderRadius.full.mobile});
  cursor: pointer;
  display: inline-flex;
  font-family: var(${themeVariables.typography.body.fontFamily}, sans-serif);
  font-size: var(${themeVariables.typography.body.sizes.md.fontSize});
  font-weight: 700;
  justify-content: center;
  line-height: 1;
  padding: var(${themeVariables.spacing.sm}) var(${themeVariables.spacing.lg});
  text-align: center;
  text-decoration: none;

  &[disabled],
  &[aria-disabled='true'] {
    cursor: not-allowed;
  }
`;

export const primary = css`
  ${createVariantStyles({
    backgroundColor: `var(${themeVariables.colors.surface.interactive})`,
    color: `var(${themeVariables.colors.page.default})`,
  })}
`;

export const primaryInverted = css`
  ${createVariantStyles({
    backgroundColor: `var(${themeVariables.colors.page.default})`,
    color: `var(${themeVariables.colors.surface.interactive})`,
  })}
`;

export const secondaryDefault = css`
  ${createVariantStyles({
    backgroundColor: `transparent`,
    borderColor: `var(${themeVariables.colors.stroke.interactive})`,
    color: `var(${themeVariables.colors.content.interactive})`,
  })}
`;

export const secondaryInverted = css`
  ${createVariantStyles({
    backgroundColor: 'transparent',
    borderColor: `var(${themeVariables.colors.stroke.interactive})`,
    color: `var(${themeVariables.colors.page.default})`,
  })}
`;

export const getVariantStyles = ({
  inverted = false,
  secondary = false,
}: {
  inverted?: boolean;
  secondary?: boolean;
}) => {
  if (secondary) {
    return inverted ? secondaryInverted : secondaryDefault;
  }

  return inverted ? primaryInverted : primary;
};

export const compact = css`
  font-size: var(${themeVariables.typography.body.sizes.sm.fontSize});
  padding: var(${themeVariables.spacing.xs}) var(${themeVariables.spacing.md});
`;

export const fullWidth = css`
  display: flex;
  width: 100%;
`;
